import { supabase } from '../lib/supabase';

interface ErrorReport {
  error_type: string;
  error_message: string;
  error_stack?: string;
  error_code?: string;
  user_id?: string;
  user_email?: string;
  organization_id?: string;
  page_url: string;
  user_agent: string;
  timestamp: string;
  additional_data?: Record<string, any>;
}

interface EmailPayload {
  to: string;
  subject: string;
  html: string;
}

class ErrorReporterService {
  // Using verified Resend email (change to lewisvillamor26@gmail.com after domain verification)
  private readonly REPORT_EMAIL = 'anoosh.cyrus@alasvand.com.au';
  private readonly ERROR_LOG_TABLE = 'error_logs';
  private sentErrors: Set<string> = new Set();
  private readonly DUPLICATE_WINDOW = 60000; // 1 minute

  /**
   * Report an error - logs to database and sends email
   */
  async reportError(
    error: Error | any,
    context?: {
      errorCode?: string;
      errorType?: string;
      additionalData?: Record<string, any>;
    }
  ): Promise<void> {
    try {
      // Get current user info
      const { data: { user } } = await supabase.auth.getUser();

      let userEmail = user?.email;
      let userId = user?.id;
      let organizationId: string | undefined;

      if (userId) {
        const { data: userData } = await supabase
          .from('users')
          .select('organization_id')
          .eq('id', userId)
          .single();

        organizationId = userData?.organization_id;
      }

      // Create error report
      const errorReport: ErrorReport = {
        error_type: context?.errorType || error.name || 'UnknownError',
        error_message: error.message || String(error),
        error_stack: error.stack,
        error_code: context?.errorCode || this.extractErrorCode(error),
        user_id: userId,
        user_email: userEmail,
        organization_id: organizationId,
        page_url: window.location.href,
        user_agent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        additional_data: context?.additionalData,
      };

      // Check for duplicates (prevent spam)
      const errorKey = `${errorReport.error_type}-${errorReport.error_message}`;
      if (this.sentErrors.has(errorKey)) {
        console.log('Duplicate error detected, skipping email');
        return;
      }

      // Mark as sent and clear after window
      this.sentErrors.add(errorKey);
      setTimeout(() => this.sentErrors.delete(errorKey), this.DUPLICATE_WINDOW);

      // Log to database
      await this.logErrorToDatabase(errorReport);

      // Send email notification
      await this.sendErrorEmail(errorReport);

      console.log('Error reported successfully:', errorReport);
    } catch (reportingError) {
      // Don't let error reporting cause more errors
      console.error('Failed to report error:', reportingError);
    }
  }

  /**
   * Log error to database for tracking
   */
  private async logErrorToDatabase(errorReport: ErrorReport): Promise<void> {
    try {
      const { error } = await supabase
        .from(this.ERROR_LOG_TABLE)
        .insert({
          error_type: errorReport.error_type,
          error_message: errorReport.error_message,
          error_stack: errorReport.error_stack,
          error_code: errorReport.error_code,
          user_id: errorReport.user_id,
          organization_id: errorReport.organization_id,
          page_url: errorReport.page_url,
          user_agent: errorReport.user_agent,
          additional_data: errorReport.additional_data,
          created_at: errorReport.timestamp,
        });

      if (error) {
        console.error('Failed to log error to database:', error);
      }
    } catch (err) {
      console.error('Error logging to database:', err);
    }
  }

  /**
   * Send error notification email
   */
  private async sendErrorEmail(errorReport: ErrorReport): Promise<void> {
    try {
      const emailHtml = this.generateErrorEmailHTML(errorReport);

      const emailPayload: EmailPayload = {
        to: this.REPORT_EMAIL,
        subject: `🚨 DealerOS Error: ${errorReport.error_type} - ${errorReport.error_code || 'N/A'}`,
        html: emailHtml,
      };

      // Call Supabase Edge Function to send email
      const { data, error } = await supabase.functions.invoke('send-error-email', {
        body: emailPayload,
      });

      if (error) {
        console.error('Failed to send error email:', error);
        // Fallback: Try direct API call if edge function fails
        await this.sendEmailFallback(emailPayload);
      } else {
        console.log('Error email sent successfully');
      }
    } catch (err) {
      console.error('Error sending email:', err);
    }
  }

  /**
   * Fallback email sending (direct API call)
   */
  private async sendEmailFallback(emailPayload: EmailPayload): Promise<void> {
    // This is a fallback - you can implement direct API call to email service
    // For now, just log that we need to send the email
    console.warn('Email fallback needed:', emailPayload);

    // You could call a service like SendGrid, Resend, or Mailgun directly here
    // Example with fetch to your own backend endpoint:
    /*
    try {
      await fetch('/api/send-error-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(emailPayload),
      });
    } catch (err) {
      console.error('Fallback email failed:', err);
    }
    */
  }

  /**
   * Generate HTML email content
   */
  private generateErrorEmailHTML(errorReport: ErrorReport): string {
    const severity = this.getErrorSeverity(errorReport);
    const severityColor = severity === 'critical' ? '#dc2626' : severity === 'high' ? '#ea580c' : '#f59e0b';

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <style>
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; line-height: 1.6; color: #333; }
          .container { max-width: 600px; margin: 0 auto; padding: 20px; }
          .header { background: ${severityColor}; color: white; padding: 20px; border-radius: 8px 8px 0 0; }
          .content { background: #f9fafb; padding: 20px; border: 1px solid #e5e7eb; border-radius: 0 0 8px 8px; }
          .section { background: white; padding: 15px; margin: 10px 0; border-radius: 6px; border-left: 4px solid ${severityColor}; }
          .label { font-weight: 600; color: #6b7280; font-size: 12px; text-transform: uppercase; margin-bottom: 5px; }
          .value { color: #111827; margin-bottom: 15px; }
          .code { background: #1f2937; color: #f3f4f6; padding: 15px; border-radius: 6px; overflow-x: auto; font-family: 'Courier New', monospace; font-size: 12px; white-space: pre-wrap; word-wrap: break-word; }
          .badge { display: inline-block; padding: 4px 12px; border-radius: 12px; font-size: 12px; font-weight: 600; text-transform: uppercase; }
          .badge-critical { background: #fef2f2; color: #dc2626; }
          .badge-high { background: #fff7ed; color: #ea580c; }
          .badge-medium { background: #fefce8; color: #f59e0b; }
          .footer { text-align: center; margin-top: 20px; color: #6b7280; font-size: 12px; }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h1 style="margin: 0; font-size: 24px;">🚨 DealerOS Error Detected</h1>
            <p style="margin: 5px 0 0 0; opacity: 0.9;">Automatic Error Report</p>
          </div>

          <div class="content">
            <div class="section">
              <div class="label">Severity</div>
              <span class="badge badge-${severity}">${severity.toUpperCase()}</span>
            </div>

            <div class="section">
              <div class="label">Error Type</div>
              <div class="value"><strong>${errorReport.error_type}</strong></div>

              ${errorReport.error_code ? `
                <div class="label">Error Code</div>
                <div class="value"><code>${errorReport.error_code}</code></div>
              ` : ''}

              <div class="label">Error Message</div>
              <div class="value">${this.escapeHtml(errorReport.error_message)}</div>
            </div>

            ${errorReport.error_stack ? `
              <div class="section">
                <div class="label">Stack Trace</div>
                <div class="code">${this.escapeHtml(errorReport.error_stack)}</div>
              </div>
            ` : ''}

            <div class="section">
              <div class="label">User Information</div>
              <div class="value">
                ${errorReport.user_email ? `<strong>Email:</strong> ${errorReport.user_email}<br>` : ''}
                ${errorReport.user_id ? `<strong>User ID:</strong> ${errorReport.user_id}<br>` : ''}
                ${errorReport.organization_id ? `<strong>Organization ID:</strong> ${errorReport.organization_id}<br>` : ''}
                ${!errorReport.user_email && !errorReport.user_id ? '<em>Anonymous/Not logged in</em>' : ''}
              </div>
            </div>

            <div class="section">
              <div class="label">Context</div>
              <div class="value">
                <strong>Page:</strong> ${errorReport.page_url}<br>
                <strong>Timestamp:</strong> ${new Date(errorReport.timestamp).toLocaleString()}<br>
                <strong>User Agent:</strong> ${errorReport.user_agent}
              </div>
            </div>

            ${errorReport.additional_data && Object.keys(errorReport.additional_data).length > 0 ? `
              <div class="section">
                <div class="label">Additional Data</div>
                <div class="code">${JSON.stringify(errorReport.additional_data, null, 2)}</div>
              </div>
            ` : ''}

            <div class="footer">
              <p>This is an automated error report from DealerOS.</p>
              <p>Timestamp: ${new Date(errorReport.timestamp).toISOString()}</p>
            </div>
          </div>
        </div>
      </body>
      </html>
    `;
  }

  /**
   * Extract error code from error object
   */
  private extractErrorCode(error: any): string | undefined {
    if (error.code) return error.code;
    if (error.status) return `HTTP_${error.status}`;

    // Check for Supabase/PostgREST error codes
    const message = error.message || String(error);
    const pgrstMatch = message.match(/PGRST\d+/);
    if (pgrstMatch) return pgrstMatch[0];

    return undefined;
  }

  /**
   * Determine error severity
   */
  private getErrorSeverity(errorReport: ErrorReport): 'critical' | 'high' | 'medium' {
    const { error_code, error_type, error_message } = errorReport;

    // Critical errors
    if (error_code?.includes('500')) return 'critical';
    if (error_code?.startsWith('PGRST')) return 'critical';
    if (error_type?.includes('Database')) return 'critical';
    if (error_message?.toLowerCase().includes('failed to fetch')) return 'critical';

    // High priority errors
    if (error_code?.includes('400') || error_code?.includes('403') || error_code?.includes('404')) return 'high';
    if (error_type?.includes('Network')) return 'high';

    return 'medium';
  }

  /**
   * Escape HTML for safe display
   */
  private escapeHtml(text: string): string {
    const map: Record<string, string> = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    };
    return text.replace(/[&<>"']/g, (m) => map[m]);
  }

  /**
   * Report API error (for API calls that fail)
   */
  async reportAPIError(
    error: any,
    endpoint: string,
    method: string = 'GET'
  ): Promise<void> {
    await this.reportError(error, {
      errorType: 'API_Error',
      errorCode: error.code || error.status?.toString(),
      additionalData: {
        endpoint,
        method,
        statusCode: error.status,
        statusText: error.statusText,
      },
    });
  }

  /**
   * Report Supabase error
   */
  async reportSupabaseError(
    error: any,
    operation: string,
    table?: string
  ): Promise<void> {
    await this.reportError(error, {
      errorType: 'Supabase_Error',
      errorCode: error.code,
      additionalData: {
        operation,
        table,
        hint: error.hint,
        details: error.details,
      },
    });
  }
}

export const errorReporter = new ErrorReporterService();
